import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Key } from '@phosphor-icons/react';
import { mapAuthError, type MappedApiError } from '@mybusinessmate/api-client';
import { OTP_LENGTH } from '@mybusinessmate/schema';
import { AuthShell, STAFF_BRAND } from '../../../../components/AuthShell';
import { AuthHeading } from '../../../../components/auth/auth-heading';
import { Banner } from '../../../../components/ui/banner';
import { Button } from '../../../../components/ui/button';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Input } from '../../../../components/ui/input';
import { OtpInput } from '../../../../components/ui/otp-input';
import { textLinkClass } from '../../../../components/ui/text-link';
import { authClient } from '../../../../lib/api';
import { normalizeBackupCode } from '../../../../lib/backup-code';
import { isStaffRole, refreshSession } from '../../../../lib/session';

export const Route = createFileRoute('/subdomain/admin/_auth/two-factor')({
  component: StaffTwoFactorPage,
});

/** Staff second step (Figma A01.4): the 6-digit authenticator code, or one of the saved backup codes. */
function StaffTwoFactorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [useBackup, setUseBackup] = useState(false);
  const [code, setCode] = useState('');
  // Off unless chosen: a staff browser that skips the second step for 30 days should be a decision, not a default.
  const [trustDevice, setTrustDevice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<MappedApiError>();
  const [notStaff, setNotStaff] = useState(false);

  const submit = async (value: string) => {
    setSubmitting(true);
    setProblem(undefined);
    setNotStaff(false);
    const { error } = useBackup
      ? await authClient.twoFactor.verifyBackupCode({ code: normalizeBackupCode(value), trustDevice })
      : await authClient.twoFactor.verifyTotp({ code: value.trim(), trustDevice });
    const mapped = mapAuthError(error);
    if (mapped) {
      setProblem(mapped);
      setSubmitting(false);
      return;
    }

    const session = await refreshSession(queryClient);
    if (!session || !isStaffRole(session.user.role)) {
      // A client account has no business in the staff app, and must not leave a session behind that blocks the real staff sign-in.
      if (session) await authClient.signOut();
      await refreshSession(queryClient);
      setNotStaff(true);
      setSubmitting(false);
      return;
    }
    await navigate({ to: '/subdomain/admin' });
  };

  const wrongCode = problem?.kind === 'invalid_code';
  const blocking = problem && !wrongCode ? problem : undefined;
  const ready = useBackup ? code.trim().length > 0 : code.length === OTP_LENGTH;

  return (
    <AuthShell brand={STAFF_BRAND}>
      <AuthHeading
        title={useBackup ? 'Use a backup code' : 'Two-step sign-in'}
        subtitle={
          useBackup
            ? 'Enter one of the 10 backup codes you saved when you turned on two-step sign-in. Each code works once.'
            : 'Enter the 6-digit code from your authenticator app.'
        }
      />

      {notStaff ? (
        <Banner tone="error">This sign-in is for MyBusinessMate staff. Clients sign in at the client app.</Banner>
      ) : null}

      {blocking ? (
        <Banner tone="error" title={blocking.kind === 'two_factor_locked' ? 'Too many wrong codes' : undefined}>
          {blocking.kind === 'two_factor_locked'
            ? 'Two-step sign-in is locked for a short while. Try again in about 15 minutes, or ask an admin to reset it.'
            : blocking.kind === 'two_factor_expired'
              ? 'Your sign-in expired. Start again from the sign-in page.'
              : blocking.message}
        </Banner>
      ) : null}

      {useBackup ? (
        <Input
          label="Backup code"
          leadingIcon={Key}
          autoComplete="one-time-code"
          autoCapitalize="none"
          spellCheck={false}
          autoFocus
          value={code}
          error={wrongCode ? 'That code didn’t work or was already used. Check it and try another one.' : undefined}
          helperText="Capitals matter, the dash is optional."
          onChange={(e) => {
            setCode(e.target.value);
            setProblem(undefined);
          }}
        />
      ) : (
        <div className="flex flex-col items-center gap-(--mbm-space-3)">
          <OtpInput
            value={code}
            onChange={(value) => {
              setCode(value);
              setProblem(undefined);
            }}
            onComplete={(value) => void submit(value)}
            disabled={submitting}
            error={wrongCode}
            autoFocus
          />
          {wrongCode ? (
            <p role="alert" className="text-body-small text-feedback-error">
              That code didn’t work. Try the newest one in your app.
            </p>
          ) : (
            <p className="text-body-small text-text-tertiary">Codes refresh every 30 seconds.</p>
          )}
        </div>
      )}

      <div className="rounded-(--mbm-radius-md) border border-border-input p-(--mbm-space-4)">
        <Checkbox
          checked={trustDevice}
          onChange={(e) => setTrustDevice(e.target.checked)}
          label={
            <span className="flex flex-col">
              <span className="text-label-medium text-text-primary">Trust this browser</span>
              <span className="text-body-xsmall text-text-tertiary">Skip codes on this computer for 30 days</span>
            </span>
          }
        />
      </div>

      <Button fullWidth disabled={!ready} loading={submitting} onClick={() => void submit(code)}>
        Verify
      </Button>

      <div className="flex flex-col gap-(--mbm-space-4)">
        <button
          type="button"
          className={`${textLinkClass} self-start`}
          onClick={() => {
            setUseBackup((v) => !v);
            setCode('');
            setProblem(undefined);
          }}
        >
          {useBackup ? 'Use your authenticator app instead' : 'Use a backup code instead'}
        </button>
        {useBackup ? (
          <p className="text-body-xsmall text-text-tertiary">
            Lost your phone and your codes? Ask an admin to reset two-step sign-in for you.
          </p>
        ) : null}
        {problem?.kind === 'two_factor_expired' ? (
          <Link to="/subdomain/admin/admin-login" className={`${textLinkClass} self-start`}>
            Back to sign in
          </Link>
        ) : null}
      </div>
    </AuthShell>
  );
}
